import { useNavigate } from "@tanstack/react-router";
import {
  PartnerEvents,
  getPartnerEventName,
  getControlCategoryName,
  ControlCategories,
  ValidControlCategory,
  assertNever,
  ValidPartnerEventCode,
} from "@theprelude/core";
import { z } from "zod";
import { DefaultOverlay } from "@/components/default-overlay";
import { Filter, filtersSchema } from "@/lib/filter";
import {
  validControlCategorySchema,
  validPartnerEventSchema,
} from "@/lib/validators";
import { ControlCategoryFailureEndpointsTable } from "../endpoint-tables/control-category-failure-endpoints-table";

const overlay = "control-category-failures";

export const controlCategoryFailuresSchema = z.tuple([
  z.literal(overlay),
  z.object({
    category: validControlCategorySchema,
    partnerEvent: validPartnerEventSchema.optional().catch(1),
    filters: filtersSchema.optional().default([]),
  }),
]);

type ControlCategoryFailuresSchema = z.infer<
  typeof controlCategoryFailuresSchema
>[1];

export const ControlCategoryFailuresOverlay = (
  props: ControlCategoryFailuresSchema,
) => {
  const title = getTitle(props);

  const { partnerEvent } = props;

  const navigate = useNavigate();

  const onFilter = (filters: Filter[]) =>
    navigate({
      to: window.location.pathname,
      params: (prev) => prev,
      search: (prev) => ({
        ...prev,
        overlay: [overlay, { ...props, filters }],
      }),
    });

  const { category } = props;

  return (
    <DefaultOverlay title={title} defaultSize={60}>
      <div className="flex h-full flex-col gap-4 p-4 pb-0">
        {partnerEvent !== undefined && (
          <div className="flex-1 overflow-hidden">
            <ControlCategoryFailureEndpointsTable
              {...props}
              category={category}
              partnerEvent={partnerEvent}
              initialQuery={getInitialQuery(partnerEvent)}
              onFilter={onFilter}
            />
          </div>
        )}
      </div>
    </DefaultOverlay>
  );
};

const getTitle = (props: ControlCategoryFailuresSchema) => {
  const { category, partnerEvent } = props;

  const categoryName = getControlCategoryName(category);
  const resourceType = getResourceType(category);

  const eventName =
    partnerEvent === PartnerEvents.USER_MISSING_ASSET_MANAGER ||
    partnerEvent === PartnerEvents.USER_MISSING_EDR ||
    partnerEvent === PartnerEvents.USER_MISSING_VULN_MANAGER
      ? "No Registered Devices"
      : getPartnerEventName(partnerEvent ?? PartnerEvents.INVALID);

  return `${categoryName} ${resourceType} with ${eventName}`;
};

const getResourceType = (category: ValidControlCategory) => {
  switch (category) {
    case ControlCategories.ASSET_MANAGER:
    case ControlCategories.CLOUD:
    case ControlCategories.DISCOVERED_DEVICES:
    case ControlCategories.SIEM:
    case ControlCategories.VULN_MANAGER:
    case ControlCategories.XDR: {
      return "Endpoints";
    }
    case ControlCategories.EMAIL: {
      return "Inboxes";
    }
    case ControlCategories.IDENTITY: {
      return "Users";
    }
    case ControlCategories.NETWORK:
    case ControlCategories.PRIVATE_REPO: {
      return "";
    }
    default: {
      return assertNever(category);
    }
  }
};

export const getInitialQuery = (
  partnerEvent: ValidPartnerEventCode,
): Record<any, any> => {
  switch (partnerEvent) {
    case PartnerEvents.NO_EDR: {
      return { missing_edr: true };
    }
    case PartnerEvents.NO_ASSET_MANAGER: {
      return { missing_asset_manager: true };
    }
    case PartnerEvents.NO_VULN_MANAGER: {
      return { missing_vuln_manager: true };
    }
    case PartnerEvents.MISSING_SCAN: {
      return { missing_vuln_scan: true };
    }
    default: {
      return {};
    }
  }
};
